/**
 * FileShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { Card, Upload, Button, Typography, message } from 'antd'
import type { UploadProps, UploadFile } from 'antd'
import { UploadOutlined, InboxOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title } = Typography

const cardOuter = { display: 'flex' as const, flexDirection: 'column' as const, overflow: 'hidden' }
const cardBody = { body: { padding: 10, flex: 1, minHeight: 0, overflow: 'auto' as const } }
const draggerCardBody = {
  body: {
    padding: 10,
    flex: 1,
    minHeight: 0,
    display: 'flex' as const,
    flexDirection: 'column' as const
  }
}

/**
 * ============================================================================
 * FileShowcase — antd Upload / 다운로드 트리거 카탈로그
 * ============================================================================
 *
 * 【역할】 antd Upload variant 5종 + 클라이언트 사이드 blob 다운로드 패턴을
 *         한 화면에 나열. 모두 `beforeUpload: () => false` 로 실제 업로드는
 *         차단된 데모용.
 *
 * 【시연 항목】
 *   - Basic Upload       — 단일 파일 버튼 (showUploadList=false)
 *   - Dragger            — 드래그 앤 드롭 영역
 *   - List Upload        — 다중 파일 목록 표시
 *   - Picture Card       — 이미지 썸네일 카드 (max 8)
 *   - Max Count + Download — `maxCount=1` + Blob → a.download
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/file' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd Upload/Button/Typography/message, @ant-design/icons
 * 【참고】 https://ant.design/components/upload
 */
export default function FileShowcase() {
  const { t } = useLabel()
  const [listFiles, setListFiles] = useState<UploadFile[]>([])
  const [pictureFiles, setPictureFiles] = useState<UploadFile[]>([])

  /**
   * 클라이언트 사이드 파일 다운로드 (서버 호출 없이 메모리 Blob 을 a[download] 로 트리거).
   * 실제 운영에서는 axios.get(url, { responseType: 'blob' }) 후 동일 패턴으로 사용.
   */
  const doDownload = () => {
    const blob = new Blob(['hello world'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sample.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  const listUploadProps: UploadProps = {
    beforeUpload: () => false,
    onChange: (info) => setListFiles(info.fileList),
    fileList: listFiles,
    multiple: true
  }

  const pictureCardProps: UploadProps = {
    listType: 'picture-card',
    beforeUpload: () => false,
    onChange: (info) => setPictureFiles(info.fileList),
    fileList: pictureFiles,
    onPreview: (file) => message.info(`${t('LBL_PREVIEW', '미리보기')}: ${file.name}`)
  }

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flex: '0 0 auto' }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_FILE_UPLOAD_DOWNLOAD', 'File 업로드/다운로드')}
        </Title>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "basic basic dragger dragger dragger dragger"
            "list list picture picture download download"
          `,
          gap: 10
        }}
      >
        <Card
          size="small"
          title="Basic Upload"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'basic' }}
        >
          <Upload
            showUploadList={false}
            maxCount={1}
            beforeUpload={(f) => {
              message.info(`${t('LBL_SELECTED', '선택')}: ${f.name}`)
              return false
            }}
          >
            <Button icon={<UploadOutlined />}>{t('BTN_SELECT_FILE', '파일 선택')}</Button>
          </Upload>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 8, fontSize: 12 }}
          >
            {t('LBL_SINGLE_FILE_BUTTON_UPLOAD', '단일 파일 버튼 업로드')}
          </Typography.Text>
        </Card>

        <Card
          size="small"
          title="Dragger (Drag & Drop)"
          styles={draggerCardBody}
          style={{ ...cardOuter, gridArea: 'dragger' }}
        >
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Upload.Dragger
              beforeUpload={() => false}
              multiple
              style={{
                flex: 1,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{ padding: '4px 8px' }}>
                <p className="ant-upload-drag-icon" style={{ margin: '0 0 4px', fontSize: 24 }}>
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text" style={{ fontSize: 12, margin: '0 0 2px' }}>
                  {t('LBL_DRAGGER_TEXT', '클릭 또는 드래그로 업로드')}
                </p>
                <p className="ant-upload-hint" style={{ fontSize: 11, margin: 0 }}>
                  {t('LBL_DRAGGER_HINT', '다중 파일 업로드를 지원합니다.')}
                </p>
              </div>
            </Upload.Dragger>
          </div>
        </Card>

        <Card
          size="small"
          title="List Upload"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'list' }}
        >
          <Upload {...listUploadProps}>
            <Button icon={<UploadOutlined />}>{t('BTN_ADD_FILE', '파일 추가')}</Button>
          </Upload>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            {t('LBL_SELECTED_FILE_COUNT', '선택된 파일 수')}: {listFiles.length}
          </div>
        </Card>

        <Card
          size="small"
          title={t('LBL_PICTURE_CARD', 'Picture Card (이미지 썸네일)')}
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'picture' }}
        >
          <Upload {...pictureCardProps}>
            {pictureFiles.length >= 8 ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 6, fontSize: 12 }}>Upload</div>
              </div>
            )}
          </Upload>
        </Card>

        <Card
          size="small"
          title="Max Count + Download"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'download' }}
        >
          <div style={{ marginBottom: 12 }}>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 4, fontSize: 12 }}
            >
              {t('LBL_MAX_COUNT_UPLOAD', '단일 파일 제한 업로드')}
            </Typography.Text>
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>{t('BTN_SELECT_ONE', '1개만 선택')}</Button>
            </Upload>
          </div>
          <div>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 4, fontSize: 12 }}
            >
              {t('LBL_FILE_DOWNLOAD', '파일 다운로드')}
            </Typography.Text>
            <Button icon={<DownloadOutlined />} onClick={doDownload}>
              {t('BTN_DOWNLOAD', '다운로드')}
            </Button>
          </div>
        </Card>
      </div>
    </PageContainer>
  )
}
